/**
 * MCP-token management (Block 4). Lists every token in the workspace the caller
 * can manage; a row opens a detail popup to edit name/role/expiry or revoke.
 * A token is NEVER owner (the role select offers only editor/viewer).
 */

import { useCallback, useEffect, useState } from "react";

import { ApiError, ryfeApi } from "../api";
import { navigate } from "../router";
import { useRyfe } from "../RyfeContext";

import "./ryfe.scss";

import type { AclRole, McpToken } from "../api";

const fmtDate = (s: string | null | undefined) =>
  s ? new Date(s).toLocaleDateString() : "—";

export const RyfeMcpTokens = () => {
  const { workspace } = useRyfe();
  const wsId = workspace?.id;
  const [tokens, setTokens] = useState<McpToken[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<McpToken | null>(null);
  const [loading, setLoading] = useState(true);
  // AP1 (D-KI) — Archiv-Filter + Zeilen-⋮-Menü.
  const [showArchived, setShowArchived] = useState(false);
  const [menuFor, setMenuFor] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!wsId) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await ryfeApi.listMcpTokens(wsId, showArchived);
      setTokens(res.tokens);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [wsId, showArchived]);

  useEffect(() => {
    load();
  }, [load]);

  const setArchived = async (t: McpToken, archived: boolean) => {
    if (!wsId) {
      return;
    }
    setMenuFor(null);
    setError(null);
    try {
      await ryfeApi.patchMcpToken(wsId, t.id, { archived });
      await load();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Konnte nicht speichern",
      );
    }
  };

  const remove = async (t: McpToken) => {
    if (!wsId) {
      return;
    }
    setMenuFor(null);
    if (
      !window.confirm(
        `Token „${t.name}“ löschen? Das kann nicht rückgängig gemacht werden.`,
      )
    ) {
      return;
    }
    setError(null);
    try {
      await ryfeApi.deleteMcpToken(wsId, t.id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Konnte nicht löschen");
    }
  };

  return (
    <div className="ryfe-page">
      <h1 className="ryfe-page__title">MCP-Tokens</h1>
      <p className="ryfe-muted">
        Zugriffstokens für den MCP-Endpunkt. Die Rolle eines Tokens lebt in der
        Ordner-ACL; ein Token kann nie Besitzer sein. Neue Tokens erstellst du
        am Ordner in „Meine Dateien“.
      </p>

      {error && (
        <div className="ryfe-auth__error" role="alert">
          {error}
        </div>
      )}

      {/* AP1 (D-KI) — Archiv-Filter: archivierte Tokens sind standardmäßig
          ausgeblendet und nur über diesen Schalter sichtbar. */}
      <label className="ryfe-check" style={{ margin: "0.4rem 0 0.8rem" }}>
        <input
          type="checkbox"
          checked={showArchived}
          onChange={(e) => setShowArchived(e.target.checked)}
        />
        <span>Archivierte Tokens anzeigen</span>
      </label>

      {loading ? (
        <div className="ryfe-muted">Loading…</div>
      ) : tokens.length === 0 ? (
        <div className="ryfe-muted">Noch keine Tokens in diesem Workspace.</div>
      ) : (
        <div className="ryfe-table-wrap">
          <table className="ryfe-table">
            <thead>
              <tr>
                <th>Name / Zweck</th>
                <th>Ordner (Scope)</th>
                <th>Rolle</th>
                <th>Läuft ab</th>
                <th>Erstellt</th>
                <th>Zuletzt benutzt</th>
                <th>Status</th>
                <th aria-label="Aktionen" />
              </tr>
            </thead>
            <tbody>
              {tokens.map((t) => (
                <tr key={t.id} className="ryfe-row">
                  <td>{t.name}</td>
                  <td>{t.folder_name || "—"}</td>
                  <td>{t.role || "—"}</td>
                  <td>{fmtDate(t.expires_at)}</td>
                  <td>{fmtDate(t.created_at)}</td>
                  <td>{fmtDate(t.last_used_at)}</td>
                  <td>
                    <span
                      className={`ryfe-badge ${
                        t.status === "active"
                          ? "ryfe-badge--ok"
                          : "ryfe-badge--warn"
                      }`}
                    >
                      {t.status === "active"
                        ? "aktiv"
                        : t.status === "archived"
                        ? "archiviert"
                        : "abgelaufen"}
                    </span>
                  </td>
                  {/* AP1 (D-KI) — 3-Punkte-Menü je Token-Zeile. */}
                  <td>
                    <div className="ryfe-elmenu__wrap">
                      <button
                        className="ryfe-chip"
                        aria-label={`Aktionen für ${t.name}`}
                        aria-haspopup="menu"
                        onClick={() =>
                          setMenuFor((cur) => (cur === t.id ? null : t.id))
                        }
                      >
                        ⋮
                      </button>
                      {menuFor === t.id && (
                        <div className="ryfe-elmenu__sub" role="menu">
                          <button
                            className="ryfe-elmenu__item"
                            onClick={() => {
                              setMenuFor(null);
                              setSelected(t);
                            }}
                          >
                            ✎ Token bearbeiten
                          </button>
                          {t.status === "archived" ? (
                            <button
                              className="ryfe-elmenu__item"
                              onClick={() => setArchived(t, false)}
                            >
                              ♻ Token reaktivieren
                            </button>
                          ) : (
                            <button
                              className="ryfe-elmenu__item"
                              onClick={() => setArchived(t, true)}
                            >
                              🗄 Token archivieren
                            </button>
                          )}
                          <button
                            className="ryfe-elmenu__item"
                            onClick={() => remove(t)}
                          >
                            🗑 Token löschen
                          </button>
                          <button
                            className="ryfe-elmenu__item"
                            onClick={() => {
                              setMenuFor(null);
                              navigate(`/ki-chat?token=${t.id}`);
                            }}
                          >
                            🤖 KI Interface öffnen
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && wsId && (
        <TokenDetail
          wsId={wsId}
          token={selected}
          onClose={() => setSelected(null)}
          onChanged={() => {
            setSelected(null);
            load();
          }}
        />
      )}
    </div>
  );
};

const TokenDetail = ({
  wsId,
  token,
  onClose,
  onChanged,
}: {
  wsId: string;
  token: McpToken;
  onClose: () => void;
  onChanged: () => void;
}) => {
  const [name, setName] = useState(token.name);
  const [role, setRole] = useState<AclRole>(
    (token.role as AclRole) || "editor",
  );
  const [expiry, setExpiry] = useState<string>(
    token.expires_at ? "keep" : "never",
  );
  const [days, setDays] = useState("30");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const patch: {
        name?: string;
        role?: AclRole;
        expiresInDays?: number | null;
      } = { name, role };
      if (expiry === "never") {
        patch.expiresInDays = null;
      } else if (expiry === "days") {
        patch.expiresInDays = Number(days);
      }
      await ryfeApi.patchMcpToken(wsId, token.id, patch);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (
      !window.confirm(
        `Token „${token.name}“ widerrufen? Das kann nicht rückgängig gemacht werden.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await ryfeApi.deleteMcpToken(wsId, token.id);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not revoke");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="ryfe-modal"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="ryfe-modal__card" onClick={(e) => e.stopPropagation()}>
        <h2 className="ryfe-panel__title">Token bearbeiten</h2>
        <label className="ryfe-field">
          <span>Name / Zweck</span>
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="ryfe-field">
          <span>Rolle</span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as AclRole)}
          >
            <option value="editor">editor</option>
            <option value="viewer">viewer</option>
          </select>
        </label>
        <label className="ryfe-field">
          <span>Ablauf</span>
          <select value={expiry} onChange={(e) => setExpiry(e.target.value)}>
            {token.expires_at && <option value="keep">unverändert</option>}
            <option value="never">läuft nie ab</option>
            <option value="days">in N Tagen…</option>
          </select>
        </label>
        {expiry === "days" && (
          <label className="ryfe-field">
            <span>Tage</span>
            <input
              type="number"
              min={1}
              value={days}
              onChange={(e) => setDays(e.target.value)}
            />
          </label>
        )}

        {error && (
          <div className="ryfe-auth__error" role="alert">
            {error}
          </div>
        )}

        <div className="ryfe-modal__actions">
          <button
            className="ryfe-btn ryfe-btn--danger"
            onClick={revoke}
            disabled={busy}
          >
            Widerrufen
          </button>
          <span className="ryfe-topbar__spacer" />
          <button className="ryfe-btn ryfe-btn--ghost" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="ryfe-btn ryfe-btn--primary"
            onClick={save}
            disabled={busy}
          >
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
};
